// packages/shared/src/constants/bodyRegions.ts
// Body region taxonomy, aggregate groups, and ICD-10 inference for flow sheet filtering.

/** All supported body regions with display labels */
export const BODY_REGIONS: { id: string; label: string }[] = [
  { id: 'cervical', label: 'Cervical' },
  { id: 'thoracic', label: 'Thoracic' },
  { id: 'shoulder', label: 'Shoulder' },
  { id: 'elbow', label: 'Elbow' },
  { id: 'wrist', label: 'Wrist' },
  { id: 'hand', label: 'Hand' },
  { id: 'lumbar', label: 'Lumbar' },
  { id: 'sacral', label: 'Sacral' },
  { id: 'pelvic', label: 'Pelvic' },
  { id: 'hip', label: 'Hip' },
  { id: 'knee', label: 'Knee' },
  { id: 'ankle', label: 'Ankle' },
];

/** Aggregate region groups — selecting an aggregate also matches its component regions */
export const BODY_REGION_GROUPS: Record<string, string[]> = {
  upper_extremity: ['shoulder', 'elbow', 'wrist', 'hand'],
  lower_extremity: ['hip', 'knee', 'ankle'],
  spine: ['cervical', 'thoracic', 'lumbar', 'sacral', 'pelvic'],
};

/**
 * ICD-10 prefix → body region mapping.
 * Matched by longest prefix first (e.g., M75.1 matches before M75).
 * Sources: standard ICD-10-CM musculoskeletal chapter (M00-M99).
 */
const ICD10_BODY_REGION_MAP: [string, string][] = [
  // Shoulder
  ['M75', 'shoulder'],   // Shoulder lesions (rotator cuff, impingement, etc.)
  ['M25.51', 'shoulder'], // Pain in shoulder
  ['S42.0', 'shoulder'],  // Fracture of clavicle
  ['S43', 'shoulder'],    // Dislocation/sprain of shoulder
  ['S46', 'shoulder'],    // Injury of muscle/tendon at shoulder

  // Elbow
  ['M77.0', 'elbow'],    // Medial epicondylitis
  ['M77.1', 'elbow'],    // Lateral epicondylitis
  ['M25.52', 'elbow'],   // Pain in elbow
  ['S53', 'elbow'],      // Dislocation/sprain of elbow

  // Wrist/Hand
  ['M25.53', 'wrist'],   // Pain in wrist
  ['M25.54', 'hand'],    // Pain in joints of hand
  ['M19.04', 'hand'],    // Primary OA, hand
  ['M65.3', 'hand'],     // Trigger finger
  ['G56.0', 'wrist'],    // Carpal tunnel syndrome
  ['S62', 'hand'],       // Fracture of wrist/hand
  ['S63', 'wrist'],      // Dislocation/sprain of wrist/hand

  // Hip
  ['M16', 'hip'],        // OA of hip
  ['M25.55', 'hip'],     // Pain in hip
  ['M70.6', 'hip'],      // Trochanteric bursitis
  ['M76.0', 'hip'],      // Gluteal tendinitis
  ['M76.1', 'hip'],      // Psoas tendinitis
  ['S73', 'hip'],        // Dislocation/sprain of hip
  ['S76', 'hip'],        // Injury of muscle/tendon at hip/thigh

  // Knee
  ['M17', 'knee'],       // OA of knee
  ['M22', 'knee'],       // Disorders of patella
  ['M23', 'knee'],       // Internal derangement of knee (meniscus, ligament)
  ['M25.56', 'knee'],    // Pain in knee
  ['M76.4', 'knee'],     // Tibial collateral bursitis
  ['M76.5', 'knee'],     // Patellar tendinitis
  ['S83', 'knee'],       // Dislocation/sprain of knee (ACL, MCL, meniscus)

  // Ankle
  ['M19.07', 'ankle'],   // Primary OA, ankle
  ['M25.57', 'ankle'],   // Pain in ankle
  ['M76.6', 'ankle'],    // Achilles tendinitis
  ['M77.3', 'ankle'],    // Calcaneal spur
  ['M72.2', 'ankle'],    // Plantar fasciitis
  ['S93', 'ankle'],      // Dislocation/sprain of ankle

  // Lumbar
  ['M54.5', 'lumbar'],   // Low back pain
  ['M54.4', 'lumbar'],   // Lumbago with sciatica
  ['M51', 'lumbar'],     // Thoracic/lumbar disc disorders
  ['M47.81', 'lumbar'],  // Spondylosis, lumbar
  ['M48.06', 'lumbar'],  // Spinal stenosis, lumbar
  ['M54.1', 'lumbar'],   // Radiculopathy (lumbar when context)
  ['G55', 'lumbar'],     // Nerve root compressions

  // Cervical
  ['M54.2', 'cervical'], // Cervicalgia
  ['M50', 'cervical'],   // Cervical disc disorders
  ['M47.81', 'cervical'], // Spondylosis, cervical (overlap with lumbar — resolved by code specificity)
  ['M48.02', 'cervical'], // Spinal stenosis, cervical
  ['M53.0', 'cervical'], // Cervicocranial syndrome
  ['M54.12', 'cervical'], // Radiculopathy, cervical

  // Thoracic
  ['M54.6', 'thoracic'],  // Pain in thoracic spine
  ['M99.02', 'thoracic'], // Segmental dysfunction, thoracic
  ['M54.14', 'thoracic'], // Radiculopathy, thoracic

  // Sacral
  ['M99.04', 'sacral'],   // Segmental dysfunction, sacral
  ['M53.3', 'sacral'],    // Sacrococcygeal disorders

  // Pelvic
  ['M99.05', 'pelvic'],   // Segmental dysfunction, pelvic
  ['M53.2', 'pelvic'],    // Spinal instabilities (SI joint context)

  // Subluxation codes (M99.0x) — critical for chiro Medicare
  ['M99.00', 'cervical'], // Segmental dysfunction, head region
  ['M99.01', 'cervical'], // Segmental dysfunction, cervical
  ['M99.03', 'lumbar'],   // Segmental dysfunction, lumbar
];

/**
 * Infer body regions from an array of ICD-10 codes.
 * Uses longest-prefix matching against the ICD-10 → body region map.
 *
 * @param codes Array of ICD-10 code objects (must have a `code` string property)
 * @returns Deduplicated array of body region IDs
 */
export function inferBodyRegionsFromICD10(
  codes: { code: string; [key: string]: unknown }[]
): string[] {
  if (!codes || codes.length === 0) return [];

  // Sort map entries by prefix length descending for longest-match-first
  const sortedMap = [...ICD10_BODY_REGION_MAP].sort(
    (a, b) => b[0].length - a[0].length
  );

  const regions = new Set<string>();

  for (const { code } of codes) {
    if (!code) continue;
    const upper = code.toUpperCase();
    for (const [prefix, region] of sortedMap) {
      if (upper.startsWith(prefix.toUpperCase())) {
        regions.add(region);
        break; // First (longest) match wins
      }
    }
  }

  return [...regions];
}
